import { FC, useCallback, useEffect } from 'react';

import { useIntl } from 'react-intl';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { useSearchParameters } from '@/components/signup/hooks/use-search-parameters';

import { ANALYTICS_PREFIX } from '../onboarding-wizard/common/constants';
import { useWizardConfiguration } from '../onboarding-wizard/controllers';

import { useScreenManager } from '../../hooks/use-screen-manager';
import { getCurrentUrlWithParams } from '../../utils/utils';

import CodingAgentChoiceView from './coding-agent-choice';
import { CodingAgentId } from './types';

/**
 * Container that wires the dumb {@link CodingAgentChoiceView} into the
 * confirmation-page screen pipeline.
 *
 * On submit:
 *  - Persists the selected agent IDs as a comma-separated `coding_agents`
 *    query param so the choice survives a refresh and the screen is skipped
 *    on re-entry.
 *  - Advances the screen manager to the next screen.
 *
 * Analytics (TWCG-1935):
 *  - `viewed` (screen) when the choice screen is shown.
 *  - `toggled` (ui) when an agent is checked/unchecked.
 *  - `clicked` (ui) on the Next button.
 *  - `submitted` (ui) when the user confirms their selection.
 */
export const CodingAgentChoiceController: FC = () => {
	const { nextScreen } = useScreenManager();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { teamType, jtbd, templateChoice, signupSource } = useSearchParameters();
	const { formatMessage } = useIntl();
	const {
		projectDetails: { defaultProjectName },
	} = useWizardConfiguration();

	const projectName = formatMessage(defaultProjectName);

	useEffect(() => {
		createAnalyticsEvent({
			action: 'viewed',
			eventType: 'screen',
			name: `${ANALYTICS_PREFIX}CodingAgentChoice`,
			attributes: { teamType, jtbd, templateChoice, signupSource },
		}).fire();
	}, [createAnalyticsEvent, teamType, jtbd, templateChoice, signupSource]);

	const handleAgentToggled = useCallback(
		(agentId: CodingAgentId, selected: boolean) => {
			createAnalyticsEvent({
				action: 'toggled',
				actionSubject: `${ANALYTICS_PREFIX}CodingAgentChoiceAgent`,
				eventType: 'ui',
				attributes: {
					agentId,
					selected,
					teamType,
					jtbd,
					templateChoice,
					signupSource,
				},
			}).fire();
		},
		[createAnalyticsEvent, teamType, jtbd, templateChoice, signupSource],
	);

	const handleSubmit = useCallback(
		(selected: CodingAgentId[]) => {
			const agentsValue = selected.length > 0 ? selected.join(',') : 'NONE';

			createAnalyticsEvent({
				action: 'clicked',
				actionSubject: `${ANALYTICS_PREFIX}CodingAgentChoiceNextButton`,
				eventType: 'ui',
				attributes: { teamType, jtbd, templateChoice, signupSource },
			}).fire();

			createAnalyticsEvent({
				action: 'submitted',
				actionSubject: `${ANALYTICS_PREFIX}CodingAgentChoice`,
				eventType: 'ui',
				attributes: {
					selectedAgents: selected,
					selectedCount: selected.length,
					teamType,
					jtbd,
					templateChoice,
					signupSource,
				},
			}).fire();

			const updatedUrl = getCurrentUrlWithParams({
				coding_agents: agentsValue,
			});

			window.history.replaceState({}, '', updatedUrl);
			nextScreen();
		},
		[createAnalyticsEvent, nextScreen, teamType, jtbd, templateChoice, signupSource],
	);

	return (
		<CodingAgentChoiceView
			projectName={projectName}
			onSubmit={handleSubmit}
			onAgentToggled={handleAgentToggled}
		/>
	);
};
